import { useFont, useRadius } from '@octane/spark'
import styled, { css } from 'styled-components'

export const Wrapper = styled.div`
    position: relative;
    width: 100%;
`

export const Field = styled.div<{
    error?: boolean
    disabled?: boolean
}>`
    display: flex;
    align-items: stretch;
    height: 60px;
    background-color: ${(props) => props.theme.colors.monochrome.white};
    border: 1px solid ${(props) => props.theme.colors.monochrome.graphite};
    border-radius: ${() => useRadius()};
    transition: border-color 120ms ease;

    &:focus-within {
        border-color: ${(props) => props.theme.colors.brand.primary};
    }

    ${(props) =>
        props.error &&
        css`
            border-color: ${props.theme.colors.alert.warning};
        `}

    ${(props) =>
        props.disabled &&
        css`
            border-color: ${props.theme.colors.monochrome.concrete};
            background-color: ${props.theme.states.disabled.light.value};
        `}
`

export const InputWrap = styled.div`
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
`

export const StyledInput = styled.input`
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    padding: 0 40px 0 20px;
    ${() => useFont('c18', { weight: '700' })}
    color: ${(props) => props.theme.colors.monochrome.jetlineBlack};

    ::placeholder {
        ${() => useFont('c18', { italic: true, weight: '400' })}
        color: ${(props) => props.theme.colors.monochrome.graphite};
    }

    :disabled {
        cursor: not-allowed;
    }
`

export const ClearButton = styled.button`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 50%;
    background-color: ${(props) => props.theme.colors.monochrome.smoke};
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    :hover {
        background-color: ${(props) => props.theme.colors.monochrome.concrete};
    }
`

export const SearchButton = styled.button`
    flex: none;
    width: 60px;
    border: none;
    border-left: 1px solid ${(props) => props.theme.colors.monochrome.concrete};
    border-radius: 0 ${() => useRadius()} ${() => useRadius()} 0;
    background-color: ${(props) => props.theme.colors.brand.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    :disabled {
        cursor: not-allowed;
        background-color: ${(props) => props.theme.colors.monochrome.concrete};
    }
`

export const OptionsPanel = styled.div`
    font-family: ${(props) => props.theme.fonts.secondary};
    width: var(--input-width);
    background-color: ${(props) => props.theme.colors.monochrome.white};
    padding: 0.625em;
    max-height: 280px;
    overflow: auto;
    z-index: 10;
    box-shadow: ${(props) => props.theme.elevations.modal};
    border-radius: ${() => useRadius()};

    &:focus {
        outline: 2px solid transparent;
        outline-offset: 2px;
    }
`

export const Option = styled.div<{ focus?: boolean; selected?: boolean }>`
    ${() => useFont('c16')}
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 9px 10px;
    border-radius: ${() => useRadius()};
    cursor: pointer;
    color: ${(props) => props.theme.colors.monochrome.ravenSub};

    ${(props) =>
        (props.focus || props.selected) &&
        css`
            background-color: ${props.theme.states.interaction.alternative
                .value};
            color: ${props.theme.states.interaction.primary.hover.value};
        `}

    mark {
        background: none;
        padding: 0;
        color: ${(props) => props.theme.colors.brand.primary};
        font-weight: 700;
    }
`

export const OptionMeta = styled.span`
    ${() => useFont('c12', { weight: '700' })}
    color: ${(props) => props.theme.colors.monochrome.graphite};
`

export const EmptyState = styled.div`
    padding: 12px 10px;
    color: ${(props) => props.theme.colors.monochrome.graphite};

    strong {
        display: block;
        color: ${(props) => props.theme.colors.monochrome.ravenSub};
        ${() => useFont('c14', { weight: '700' })}
        margin-bottom: 2px;
    }
`

export const PrefilledField = styled.div<{ isPlaceholder?: boolean }>`
    display: flex;
    align-items: center;
    height: 60px;
    padding: 0 20px;
    background-color: ${(props) => props.theme.colors.form.moonGrey};
    border: 1px solid ${(props) => props.theme.colors.form.moonGrey};
    border-radius: ${() => useRadius()};
    ${() => useFont('c18', { weight: '700' })}
    color: ${(props) => props.theme.colors.monochrome.jetlineBlack};

    ${(props) =>
        props.isPlaceholder &&
        css`
            ${() => useFont('c18', { italic: true, weight: '400' })}
            color: ${props.theme.colors.monochrome.graphite};
        `}
`
